import { useState } from 'react';
import { useStartDate } from '../StartDateContext';
import { useLoading } from '../LoadingContext';
import { useConfirmAlert } from './useConfirmAlert';
import { usePlanName } from '../PlanNameContext';
import { useHasChanges } from '../HasChangesContext';
import { usePlanMetadata } from '../PlanMetadataContext';
import { savePlan, updatePlan, fetchPlan, fetchPublishedPlan } from '../api';

export const useSaveLoad = (panelGroupsContainerRef, user, onOpenAllPanels, visibleWeeks = 10, onDataLoaded = null, undoRedo = null) => {
  // Hilfsfunktion: Generiert neue UUID für Tasks beim Anhängen
  const generateTaskUUID = () => {
    return 'task-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 18);
  };

  const { startDate, setStartDate } = useStartDate();
  const { showLoading, hideLoading } = useLoading();
  const { alert: modalAlert } = useConfirmAlert();
  const { 
    currentPlanName, 
    createNewPlan, 
    loadPlan: setPlanNameFromLoad, 
    updatePlanName 
  } = usePlanName();
  const { markAsSaved } = useHasChanges();
  const { updatePlanMetadata } = usePlanMetadata();
  
  // Wrapper function to handle async alerts without await
  const showAlert = (message, options = {}) => {
    modalAlert(message, options).catch(console.error);
  };
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showLoadConfirmDialog, setShowLoadConfirmDialog] = useState(false);
  const [showDatabaseSaveDialog, setShowDatabaseSaveDialog] = useState(false);
  const [loadMode, setLoadMode] = useState('replace');
  const [jsonData, setJsonData] = useState('');
  const [loadJsonData, setLoadJsonData] = useState('');
  const [saveFileName, setSaveFileName] = useState('');
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [isUpdatingExistingPlan, setIsUpdatingExistingPlan] = useState(false);
  const [existingPlanId, setExistingPlanId] = useState(null);
  
  // Ctrl+S Tracking
  const [currentLoadedPlanId, setCurrentLoadedPlanId] = useState(null);
  const [currentLoadedPlanVersion, setCurrentLoadedPlanVersion] = useState(null);
  const [hasCreatedVersionAfterLoad, setHasCreatedVersionAfterLoad] = useState(false);

  // Hilfsfunktion: Verschiebt alle Datumsfelder im Plan um eine bestimmte Anzahl Tage
  const shiftPlanDates = (saveData, daysDifference) => {
    const shiftDate = (dateStr) => {
      if (!dateStr) return dateStr;
      const date = new Date(dateStr);
      date.setDate(date.getDate() + daysDifference);
      return date.toISOString();
    };

    const newData = JSON.parse(JSON.stringify(saveData)); // Deep clone

    // Verschiebe globales startDate
    if (newData.startDate) {
      newData.startDate = shiftDate(newData.startDate);
    }

    // Verschiebe alle movableBars
    if (newData.panelGroups) {
      const shiftPanels = (panels) => {
        panels.forEach(panel => {
          if (panel.type === 'group' && panel.panels) {
            shiftPanels(panel.panels);
          } else if (panel.movableBars && Array.isArray(panel.movableBars)) {
            panel.movableBars.forEach(bar => {
              if (bar.startDate) bar.startDate = shiftDate(bar.startDate);
              if (bar.endDate) bar.endDate = shiftDate(bar.endDate);
            });
          }
        });
      };

      newData.panelGroups.forEach(group => {
        shiftPanels(group.panels || []);
      });
    }

    return newData;
  };

  // Hilfsfunktion: Prüft, ob bereits Daten vorhanden sind
  const hasExistingData = () => {
    if (!panelGroupsContainerRef.current) {
      return false;
    }
    
    const containerData = panelGroupsContainerRef.current.getData();
    return containerData && 
           containerData.groups && 
           Array.isArray(containerData.groups) && 
           containerData.groups.length > 0;
  };

  // Hilfsfunktion: Extrahiert alle Tasks aus den Panel-Daten
  const extractAllTasks = (panelGroups) => {
    const tasks = [];
    
    const extractFromPanels = (panels) => {
      if (!Array.isArray(panels)) return;
      
      panels.forEach(panel => {
        if (panel.type === 'group' && panel.panels) {
          // Rekursiv Untergruppen durchsuchen
          extractFromPanels(panel.panels);
        } else if (panel.movableBars && Array.isArray(panel.movableBars)) {
          // Tasks aus movableBars extrahieren
          panel.movableBars.forEach(bar => {
            if (bar.startDate && bar.endDate) {
              tasks.push({
                startDate: new Date(bar.startDate),
                endDate: new Date(bar.endDate),
                label: bar.label || 'Unbenannter Task',
                panelTitle: panel.title || 'Unbenanntes Panel'
              });
            }
          });
        }
      });
    };
    
    panelGroups.forEach(group => {
      extractFromPanels(group.panels || []);
    });
    
    return tasks;
  };

  // Hilfsfunktion: Prüft Task-Sichtbarkeit und gibt Warnung zurück
  const checkTaskVisibility = (tasks, visibleWeeks = 10) => {
    if (tasks.length === 0) return null;
    
    // Bestimme den sichtbaren Zeitraum (basierend auf aktuellem startDate)
    const viewStartDate = new Date(startDate);
    const dayOfWeek = viewStartDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
    viewStartDate.setHours(0, 0, 0, 0);
    
    const viewEndDate = new Date(viewStartDate);
    viewEndDate.setDate(viewEndDate.getDate() + (visibleWeeks * 7));
    
    // Finde alle Tasks außerhalb des sichtbaren Bereichs
    const tasksStartingBefore = tasks.filter(task => task.startDate < viewStartDate);
    const tasksEndingAfter = tasks.filter(task => task.endDate > viewEndDate);
    
    if (tasksStartingBefore.length === 0 && tasksEndingAfter.length === 0) {
      return null; // Alle Tasks sind sichtbar
    }
    
    // Finde frühestes Start- und spätestes Enddatum
    const earliestStart = tasksStartingBefore.length > 0 
      ? new Date(Math.min(...tasksStartingBefore.map(t => t.startDate.getTime())))
      : null;
      
    const latestEnd = tasksEndingAfter.length > 0 
      ? new Date(Math.max(...tasksEndingAfter.map(t => t.endDate.getTime())))
      : null;
    
    return {
      hasInvisibleTasks: true,
      tasksStartingBefore: tasksStartingBefore.length,
      tasksEndingAfter: tasksEndingAfter.length,
      currentViewStart: viewStartDate,
      currentViewEnd: viewEndDate,
      earliestTaskStart: earliestStart,
      latestTaskEnd: latestEnd,
      invisibleTasks: [...tasksStartingBefore, ...tasksEndingAfter]
    };
  };

  // Speichern-Funktionalität
  const handleSave = async () => {
    showLoading('Speichere Daten...');
    
    try {
      let panelGroupsData = [];
      
      if (panelGroupsContainerRef.current) {
        const containerData = panelGroupsContainerRef.current.getData();
        panelGroupsData = containerData.groups || [];
      }

      const saveData = {
        timestamp: new Date().toISOString(),
        startDate: startDate.toISOString(),
        version: "1.0",
        panelGroups: panelGroupsData
      };
      
      const jsonDataString = JSON.stringify(saveData, null, 2);
      setJsonData(jsonDataString);
      
      // File System Access API - funktioniert nur über HTTPS oder localhost
      if (window.showSaveFilePicker && typeof window.showSaveFilePicker === 'function' && window.isSecureContext) {
        try {
          // Direkt Filebrowser öffnen
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: `${currentPlanName || 'syllabus'}.syllabus`,
            types: [{
              description: 'Syllabus-Dateien',
              accept: {
                'application/json': ['.syllabus']
              }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(jsonDataString);
          await writable.close();
          
          // Markiere als gespeichert
          markAsSaved();
          
          return; // Erfolgreich gespeichert
          
        } catch (err) {
          if (err.name === 'AbortError') {
            return; // Benutzer hat abgebrochen
          }
          // Bei anderen Fehlern: Fallback verwenden
        }
      }
      
      // Fallback: Dialog für Download (HTTP, ältere Browser)
      setSaveFileName('');
      setShowSaveDialog(true);
    } catch (error) {
      showAlert('Fehler beim Speichern der Daten:\n\n' + error.message, { title: "Fehler" });
    } finally {
      hideLoading();
    }
  };

  // Laden-Funktionalität
  const handleLoad = () => {
    setLoadMode('replace');
    setLoadJsonData('');
    setShowLoadDialog(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.syllabus') && !file.name.endsWith('.json')) {
      showAlert('Bitte wählen Sie eine .syllabus oder .json Datei aus.', { title: "Ungültige Datei" });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        JSON.parse(content);
        setLoadJsonData(content);
        
        // Extrahiere Dateiname als Plannamen für später
        const fileName = file.name.replace(/\.(syllabus|json)$/, '');
        window._pendingPlanName = fileName;
        
      } catch (error) {
        showAlert('Fehler beim Lesen der Datei: Die Datei enthält kein gültiges JSON.', { title: "Ungültige Datei" });
      }
    };
    reader.readAsText(file);
  };

  const handleCancelLoad = () => {
    setShowLoadDialog(false);
  };

  const handleLoadSubmit = () => {
    try {
      const parsedData = JSON.parse(loadJsonData);
      
      if (!parsedData.panelGroups || !Array.isArray(parsedData.panelGroups)) {
        throw new Error('Ungültige Datenstruktur: panelGroups fehlt oder ist kein Array');
      }

      // Prüfe, ob bereits Daten vorhanden sind
      if (hasExistingData()) {
        // Schließe den Load-Dialog und zeige den Confirm-Dialog
        setShowLoadDialog(false);
        setShowLoadConfirmDialog(true);
        return;
      }

      // Wenn keine Daten vorhanden sind, lade direkt ohne Abfrage
      setLoadMode('replace'); // Bei leeren Daten ist replace = append
      performLoad(parsedData);
      
    } catch (error) {
      if (error instanceof SyntaxError) {
        showAlert('Ungültiger JSON-Code. Bitte überprüfen Sie die Syntax (Kommas, Anführungszeichen, Klammern).', { title: "Ungültiges JSON" });
      } else {
        showAlert('Fehler beim Laden der Daten:\n\n' + error.message, { title: "Fehler" });
      }
    }
  };

  // Hilfsfunktion für movableBars und Panel-Zustände
  const ensureMovableBarsAndOpen = (panels, forceNewUUIDs = false) => {
    return panels.map(panel => {
      if (panel.type === 'group' && panel.panels) {
        // Recursively process subgroups
        panel.panels = ensureMovableBarsAndOpen(panel.panels, forceNewUUIDs);
        return { ...panel, open: true }; // Ensure subgroups are open
      } else if (panel.type !== 'group') {
        // Add movableBars to regular panels if missing and ensure they're open
        const movableBars = panel.movableBars || [];
        
        // UUID-Behandlung für Tasks beim Anhängen
        if (forceNewUUIDs && movableBars.length > 0) {
          movableBars.forEach(bar => {
            // Entferne veraltete assignedStudents falls vorhanden
            if (bar.assignedStudents) {
              delete bar.assignedStudents;
            }
            
            if (bar.uuid) {
              bar.uuid = generateTaskUUID();
            } else {
              // Falls keine UUID vorhanden, erstelle eine neue
              bar.uuid = generateTaskUUID();
            }
          });
        }
        
        return { 
          ...panel, 
          movableBars: movableBars,
          open: true // Ensure all panels are open after loading
        };
      }
      return { ...panel, open: true }; // Default: ensure panel is open
    });
  };

  // Führt das eigentliche Laden durch (extrahiert aus handleLoadSubmit)
  const performLoad = (parsedData, overrideLoadMode = null) => {
    const currentLoadMode = overrideLoadMode || loadMode;
    showLoading('Lade Daten...');
    
    // Deaktiviere Undo-System während des Ladens
    if (undoRedo && undoRedo.disableUndoDuringLoad) {
      undoRedo.disableUndoDuringLoad();
    }
    
    try {
      for (let i = 0; i < parsedData.panelGroups.length; i++) {
        const group = parsedData.panelGroups[i];
        if (!group.id || !group.title) {
          throw new Error(`Panel-Gruppe ${i + 1}: id oder title fehlt`);
        }
        if (!group.panels || !Array.isArray(group.panels)) {
          throw new Error(`Panel-Gruppe ${i + 1}: panels fehlt oder ist kein Array`);
        }
        
        // Bei replace-Modus: keine neuen UUIDs generieren
        group.panels = ensureMovableBarsAndOpen(group.panels, false);
      }

      if (panelGroupsContainerRef.current) {
        if (currentLoadMode === 'replace') {
          // Stelle sicher, dass alle Gruppen explizit als geöffnet markiert sind
          const groupsWithOpenState = parsedData.panelGroups.map(group => ({
            ...group,
            open: true // Beim Plan-Laden sollen alle Gruppen offen sein
          }));
          
          panelGroupsContainerRef.current.setData({
            groups: groupsWithOpenState
          });
          
          // Rufe den Callback nach dem Laden auf
          if (onDataLoaded) {
            setTimeout(onDataLoaded, 100);
          }
        } else if (currentLoadMode === 'append') {
          const existingData = panelGroupsContainerRef.current.getData();
          const existingGroups = existingData.groups || [];
          
          // Rekursive Funktion zum Ändern aller IDs (Gruppen und Panels)
          const updateIds = (panels, suffix) => {
            return panels.map(panel => {
              const newPanel = {
                ...panel,
                id: panel.id + suffix
              };
              
              // Wenn Panel eine Untergruppe ist, rekursiv verarbeiten
              if (panel.type === 'group' && panel.panels) {
                newPanel.panels = updateIds(panel.panels, suffix);
              }
              
              return newPanel;
            });
          };
          
          const suffix = '_imported_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          
          const newGroups = parsedData.panelGroups.map(group => ({
            ...group,
            id: group.id + suffix,
            title: group.title,
            open: true, // Explizit alle neuen Gruppen öffnen
            panels: ensureMovableBarsAndOpen(updateIds(group.panels || [], suffix), true) // Bei append: neue UUIDs generieren
          }));
          
          const combinedGroups = [...existingGroups, ...newGroups];
          
          panelGroupsContainerRef.current.setData({
            groups: combinedGroups
          });
          
          // Rufe den Callback nach dem Anhängen auf
          if (onDataLoaded) {
            setTimeout(onDataLoaded, 100);
          }
        }
      }

      if (parsedData.startDate && currentLoadMode === 'replace') {
        try {
          const newStartDate = new Date(parsedData.startDate);
          setStartDate(newStartDate);
        } catch (dateError) {
        }
      }
      
      // Nach dem Laden sind die Daten gespeichert (da sie aus einer Datei kommen)
      
      // Plannamen setzen falls verfügbar (für Datenbank-Loads und Datei-Loads)
      if (window._pendingPlanName) {
        setPlanNameFromLoad(window._pendingPlanName);
        delete window._pendingPlanName;
      }
      
      setShowLoadDialog(false);
      setShowLoadConfirmDialog(false);
      setLoadJsonData('');
      
      // Prüfe Task-Sichtbarkeit nach dem Laden
      const allTasks = extractAllTasks(parsedData.panelGroups);
      const visibilityCheck = checkTaskVisibility(allTasks, visibleWeeks);
      
      // Warte kurz, dann öffne explizit alle Panels
      // Das stellt sicher, dass die Panel-Zustände korrekt gesetzt sind
      if (onOpenAllPanels) {
        setTimeout(() => {
          onOpenAllPanels();
        }, 200);
      }
      
      // Mehrstufige Aktualisierung für Task-Komponenten nach dem Laden
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceTasksUpdate'));
      }, 300); // Nach onOpenAllPanels
      
      // Zeige Warnung bei unsichtbaren Tasks
      if (visibilityCheck && visibilityCheck.hasInvisibleTasks) {
        setTimeout(() => {
          const { 
            tasksStartingBefore, 
            tasksEndingAfter, 
            earliestTaskStart, 
            latestTaskEnd,
            currentViewStart,
            currentViewEnd
          } = visibilityCheck;
          
          let warningMessage = '⚠️ **Plan nicht vollständig sichtbar**\n\n';
          
          if (tasksStartingBefore > 0 && tasksEndingAfter > 0) {
            warningMessage += `Es gibt ${tasksStartingBefore} Task(s), die vor dem sichtbaren Bereich starten, und ${tasksEndingAfter} Task(s), die nach dem sichtbaren Bereich enden.\n\n`;
          } else if (tasksStartingBefore > 0) {
            warningMessage += `Es gibt ${tasksStartingBefore} Task(s), die vor dem sichtbaren Bereich starten.\n\n`;
          } else if (tasksEndingAfter > 0) {
            warningMessage += `Es gibt ${tasksEndingAfter} Task(s), die nach dem sichtbaren Bereich enden.\n\n`;
          }
          
          warningMessage += `**Aktuell sichtbarer Zeitraum:**\n${currentViewStart.toLocaleDateString('de-DE')} - ${currentViewEnd.toLocaleDateString('de-DE')}\n\n`;
          
          if (earliestTaskStart) {
            warningMessage += `**Frühester Task startet:** ${earliestTaskStart.toLocaleDateString('de-DE')}\n`;
          }
          if (latestTaskEnd) {
            warningMessage += `**Spätester Task endet:** ${latestTaskEnd.toLocaleDateString('de-DE')}\n\n`;
          }
          
          // Berechne optimale Startdaten
          let optimalStartForEarly = null;
          let optimalStartForLate = null;
          
          if (earliestTaskStart && earliestTaskStart < currentViewStart) {
            optimalStartForEarly = new Date(earliestTaskStart);
            const dayOfWeek = optimalStartForEarly.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            optimalStartForEarly.setDate(optimalStartForEarly.getDate() + mondayOffset);
            optimalStartForEarly.setHours(0, 0, 0, 0);
          }
          
          if (latestTaskEnd && latestTaskEnd > currentViewEnd) {
            optimalStartForLate = new Date(latestTaskEnd);
            optimalStartForLate.setDate(optimalStartForLate.getDate() - (visibleWeeks * 7));
            const dayOfWeek = optimalStartForLate.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            optimalStartForLate.setDate(optimalStartForLate.getDate() + mondayOffset);
            optimalStartForLate.setHours(0, 0, 0, 0);
          }
          
          // Erstelle Custom Alert mit Buttons
          const createCustomAlert = () => {
            return new Promise((resolve) => {
              // Erstelle Modal Container
              const modalDiv = document.createElement('div');
              modalDiv.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0,0,0,0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10000;
              `;
              
              // Erstelle Modal Content
              const contentDiv = document.createElement('div');
              contentDiv.style.cssText = `
                background: white;
                padding: 24px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                max-width: 500px;
                width: 90vw;
              `;
              
              // Titel
              const titleDiv = document.createElement('h3');
              titleDiv.style.cssText = `
                margin: 0 0 16px 0;
                font-size: 18px;
                color: #f57c00;
                display: flex;
                align-items: center;
                gap: 8px;
              `;
              titleDiv.innerHTML = '⚠️ Plan geladen - Sichtbarkeitswarnung';
              
              // Message
              const messageDiv = document.createElement('div');
              messageDiv.style.cssText = `
                margin-bottom: 20px;
                font-size: 14px;
                line-height: 1.5;
                white-space: pre-line;
              `;
              messageDiv.textContent = warningMessage;
              
              // Buttons Container
              const buttonsDiv = document.createElement('div');
              buttonsDiv.style.cssText = `
                display: flex;
                flex-direction: column;
                gap: 12px;
                margin-bottom: 20px;
              `;
              
              // Button für frühe Tasks
              if (optimalStartForEarly) {
                const earlyButton = document.createElement('button');
                earlyButton.style.cssText = `
                  padding: 12px 16px;
                  border: none;
                  border-radius: 6px;
                  background-color: #4caf50;
                  color: white;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: 500;
                  transition: background-color 0.2s;
                `;
                earlyButton.textContent = `📅 Startdatum auf ${optimalStartForEarly.toLocaleDateString('de-DE')} setzen (für frühe Tasks)`;
                earlyButton.onmouseover = () => earlyButton.style.backgroundColor = '#45a049';
                earlyButton.onmouseout = () => earlyButton.style.backgroundColor = '#4caf50';
                earlyButton.onclick = () => {
                  setStartDate(optimalStartForEarly);
                  document.body.removeChild(modalDiv);
                  resolve('early');
                };
                buttonsDiv.appendChild(earlyButton);
              }
              
              // Button für späte Tasks
              if (optimalStartForLate) {
                const lateButton = document.createElement('button');
                lateButton.style.cssText = `
                  padding: 12px 16px;
                  border: none;
                  border-radius: 6px;
                  background-color: #2196f3;
                  color: white;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: 500;
                  transition: background-color 0.2s;
                `;
                lateButton.textContent = `📅 Startdatum auf ${optimalStartForLate.toLocaleDateString('de-DE')} setzen (für späte Tasks)`;
                lateButton.onmouseover = () => lateButton.style.backgroundColor = '#1976d2';
                lateButton.onmouseout = () => lateButton.style.backgroundColor = '#2196f3';
                lateButton.onclick = () => {
                  setStartDate(optimalStartForLate);
                  document.body.removeChild(modalDiv);
                  resolve('late');
                };
                buttonsDiv.appendChild(lateButton);
              }
              
              // Schließen Button
              const closeButton = document.createElement('button');
              closeButton.style.cssText = `
                padding: 10px 20px;
                border: 1px solid #ccc;
                border-radius: 6px;
                background: white;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                margin-top: 8px;
              `;
              closeButton.textContent = 'Später manuell anpassen';
              closeButton.onclick = () => {
                document.body.removeChild(modalDiv);
                resolve('close');
              };
              
              // Zusammenbauen
              contentDiv.appendChild(titleDiv);
              contentDiv.appendChild(messageDiv);
              contentDiv.appendChild(buttonsDiv);
              contentDiv.appendChild(closeButton);
              modalDiv.appendChild(contentDiv);
              document.body.appendChild(modalDiv);
            });
          };
          
          // Zeige Custom Alert
          createCustomAlert().then((result) => {
            if (result === 'early' || result === 'late') {
              setTimeout(() => {
                showAlert('✅ Startdatum wurde automatisch angepasst!', {
                  title: "Startdatum angepasst"
                });
              }, 100);
            }
          });
        }, 300);
      } else if (allTasks.length > 0) {
        // Erfolgreiche Ladung ohne Sichtbarkeitsprobleme
        setTimeout(() => {
          showAlert(`✅ Plan erfolgreich geladen!\n\n${allTasks.length} Task(s) sind vollständig im sichtbaren Bereich.`, {
            title: "Plan erfolgreich geladen"
          });
        }, 300);
      }
      
      // Sanfter Refresh durch minimalen Datumswechsel (nur bei Append-Modus)
      if (loadMode === 'append') {
        setTimeout(() => {
          const currentDate = new Date(startDate);
          const triggerDate = new Date(currentDate.getTime() + 1000); // +1 Sekunde
          setStartDate(triggerDate);
          
          setTimeout(() => {
            setStartDate(currentDate); // Zurück zum Original
          }, 50);
        }, 200); // Etwas später, damit Panel-Öffnung zuerst passiert
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        showAlert('Ungültiger JSON-Code. Bitte überprüfen Sie die Syntax (Kommas, Anführungszeichen, Klammern).', { title: "Ungültiges JSON" });
      } else {
        showAlert('Fehler beim Laden der Daten:\n\n' + error.message, { title: "Fehler" });
      }
    } finally {
      hideLoading();
      
      // Aktiviere Undo-System wieder
      if (undoRedo && undoRedo.enableUndoAfterLoad) {
        undoRedo.enableUndoAfterLoad();
      }
      
      // Historie nur bei 'replace' zurücksetzen, nicht bei 'append'
      if (undoRedo && undoRedo.resetHistoryAfterLoad && currentLoadMode === 'replace') {
        // Warte kurz, damit alle Laden-Operationen abgeschlossen sind
        setTimeout(() => {
          undoRedo.resetHistoryAfterLoad();
        }, 1000);
      } else if (currentLoadMode === 'append' && undoRedo && undoRedo.saveToHistory) {
        // Bei 'append' erstelle einen neuen Undo-Punkt für den erweiterten Zustand
        setTimeout(() => {
          undoRedo.saveToHistory();
        }, 1000);
      }
    }
  };

  // Handler für LoadConfirmDialog
  const handleLoadConfirm = async () => {
    try {
      const parsedData = JSON.parse(loadJsonData);
      
      // Nur bei "replace" Modus (Überschreiben) wird der Plan gewechselt
      if (loadMode === 'replace' && window._pendingPlanName) {
        setPlanNameFromLoad(window._pendingPlanName);
        
        // Reset Ctrl+S State für geladenen Plan aus Datenbank
        // Bei Überschreiben setzen wir die geladene Plan-ID (auch wenn sie null ist)
        setCurrentLoadedPlanId(window._pendingPlanId || null);
        setHasCreatedVersionAfterLoad(false); // Beim ersten Speichern nach Load wird der bestehende Plan überschrieben
        
        // Lösche localStorage-Einträge vom alten Plan
        if (currentPlanName && user?.username) {
          const oldPlanSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
          const oldPlanIdKey = `plan_id_${currentPlanName}_${user.username}`;
          localStorage.removeItem(oldPlanSaveKey);
          localStorage.removeItem(oldPlanIdKey);
        }
      }
      // Bei "append" Modus bleibt der aktuelle Plan aktiv - kein Zustandswechsel
      
      // Cleanup (immer ausführen)
      if (window._pendingPlanName) {
        delete window._pendingPlanName;
      }
      if (window._pendingPlanId) {
        delete window._pendingPlanId;
      }
      if (window._isPublishedPlan) {
        delete window._isPublishedPlan;
      }
      
      performLoad(parsedData);
    } catch (error) {
      if (error instanceof SyntaxError) {
        showAlert('Ungültiger JSON-Code. Bitte überprüfen Sie die Syntax (Kommas, Anführungszeichen, Klammern).', { title: "Ungültiges JSON" });
      } else {
        showAlert('Fehler beim Laden der Daten:\n\n' + error.message, { title: "Fehler" });
      }
    }
  };

  const handleLoadConfirmCancel = () => {
    setShowLoadConfirmDialog(false);
    setShowLoadDialog(true); // Zurück zum Load-Dialog
  };



  // Datenbank-Speichern-Dialog öffnen
  const handleDatabaseSave = () => {
    if (!user || !user.username) {
      showAlert('Sie müssen angemeldet sein, um in die Datenbank zu speichern.', { title: "Anmeldung erforderlich" });
      return;
    }
    
    setPlanName(currentPlanName || '');
    setPlanDescription('');
    setIsUpdatingExistingPlan(false);
    setExistingPlanId(null);
    setShowDatabaseSaveDialog(true);
  };

  // Stille Speicherfunktion (ohne Dialoge für Autosave) - verwendet gleiche Logik wie handleCtrlSave
  const handleSilentSave = async () => {
    if (!user || !user.username || !currentPlanName) {
      return false; // Stilles Fehlschlagen
    }

    try {
      if (currentLoadedPlanId) {
        // Plan wurde aus Datenbank geladen
        if (!hasCreatedVersionAfterLoad) {
          // Erster Save nach Load: Neue Version erstellen
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', currentPlanName, true); // true = silent mode
          
          if (result && result.planId) {
            setHasCreatedVersionAfterLoad(true);
            // Aktualisiere currentLoadedPlanId auf die neue Version-ID
            setCurrentLoadedPlanId(result.planId);
            return true;
          } else if (result && result.success) {
            // Fallback falls keine planId zurückgegeben wird, aber Speichern erfolgreich war
            setHasCreatedVersionAfterLoad(true);
            return true;
          }
          return false;
        } else {
          // Weitere Saves nach Load: Aktuelle Version überschreiben
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true, true, currentLoadedPlanId); // explizit Update-Parameter übergeben
          const success = result && result.success;
          return success;
        }
      } else {
        // Neuer Plan oder kein geladener Plan: Prüfe localStorage für Session-Persistenz
        const planSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
        const planIdKey = `plan_id_${currentPlanName}_${user.username}`;
        const hasBeenSavedInSession = localStorage.getItem(planSaveKey) === 'true';
        const sessionPlanId = localStorage.getItem(planIdKey);
        
        if (!hasBeenSavedInSession) {
          // Erste Speicherung in dieser Session: Neuen Plan erstellen
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true); // true = silent mode
          const success = result && result.success;
          if (success) {
            localStorage.setItem(planSaveKey, 'true');
            return true;
          }
          return false;
        } else if (sessionPlanId) {
          // Plan wurde bereits in dieser Session gespeichert und wir haben die ID: Überschreiben
          const prevIsUpdating = isUpdatingExistingPlan;
          const prevExistingId = existingPlanId;
          
          setIsUpdatingExistingPlan(true);
          setExistingPlanId(parseInt(sessionPlanId, 10)); // String zu Number konvertieren
          
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true); // true = silent mode
          const success = result && result.success;
          
          setIsUpdatingExistingPlan(prevIsUpdating);
          setExistingPlanId(prevExistingId);
          
          return success;
        } else {
          // Plan wurde bereits in dieser Session gespeichert, aber keine ID vorhanden
          // Erstelle neue Version
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', currentPlanName, true); // true = silent mode
          return result && result.success;
        }
      }
    } catch (error) {
      console.error('Stilles Speichern fehlgeschlagen:', error);
      
      // Bei kritischen Netzwerkfehlern trotzdem eine Warnung anzeigen (auch im silent mode)
      if (error.message && (error.message.includes('Backend nicht erreichbar') || error.message.includes('fetch'))) {
        showAlert(
          `⚠️ **Autosave fehlgeschlagen**\n\nDas Backend ist nicht erreichbar. Ihre Änderungen konnten nicht automatisch gespeichert werden.\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
          { title: "Verbindungsfehler" }
        );
      }
      
      return false;
    }
  };

  // Ctrl+S Intelligent Save - verwendet gleiche Logik wie handleSilentSave aber mit Dialogen
  const handleCtrlSave = async () => {
    if (!user || !user.username) {
      showAlert('Sie müssen angemeldet sein, um in die Datenbank zu speichern.', { title: "Anmeldung erforderlich" });
      return { success: false };
    }

    if (!currentPlanName) {
      showAlert('Kein Plan geladen. Bitte laden Sie zuerst einen Plan oder verwenden Sie "In der Cloud speichern".', { title: "Kein Plan" });
      return { success: false };
    }

    try {
      let success = false;
      
      if (currentLoadedPlanId) {
        // Plan wurde aus Datenbank geladen
        if (!hasCreatedVersionAfterLoad) {
          // Erster Save nach Load: Neue Version erstellen
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', currentPlanName, true); // true = silent mode für Ctrl+S
          
          if (result && result.planId) {
            setHasCreatedVersionAfterLoad(true);
            // Aktualisiere currentLoadedPlanId auf die neue Version-ID
            setCurrentLoadedPlanId(result.planId);
            success = true;
          } else {
            success = result && result.success; // Fallback falls keine planId zurückgegeben wird
            if (success) {
              setHasCreatedVersionAfterLoad(true);
            }
          }
        } else {
          // Weitere Saves nach Load: Aktuelle Version überschreiben
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true, true, currentLoadedPlanId); // explizit Update-Parameter übergeben
          success = result && result.success;
        }
      } else {
        // Neuer Plan oder kein geladener Plan: Prüfe localStorage für Session-Persistenz
        const planSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
        const planIdKey = `plan_id_${currentPlanName}_${user.username}`;
        const hasBeenSavedInSession = localStorage.getItem(planSaveKey) === 'true';
        const sessionPlanId = localStorage.getItem(planIdKey);
        
        if (!hasBeenSavedInSession) {
          // Erste Speicherung in dieser Session: Neuen Plan erstellen
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true); // true = silent mode für Ctrl+S
          success = result && result.success;
          if (success) {
            localStorage.setItem(planSaveKey, 'true');
          }
        } else if (sessionPlanId) {
          // Plan wurde bereits in dieser Session gespeichert und wir haben die ID: Überschreiben
          setIsUpdatingExistingPlan(true);
          setExistingPlanId(parseInt(sessionPlanId, 10)); // String zu Number konvertieren
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', null, true); // true = silent mode für Ctrl+S
          success = result && result.success;
          setIsUpdatingExistingPlan(false);
          setExistingPlanId(null);
        } else {
          // Plan wurde bereits in dieser Session gespeichert, aber keine ID vorhanden
          // Erstelle neue Version
          const result = await handleDatabaseSaveSubmit(currentPlanName, '', currentPlanName, true); // true = silent mode für Ctrl+S
          success = result && result.success;
        }
      }
      
      return { success };
    } catch (error) {
      console.error('Ctrl+S Speichern fehlgeschlagen:', error);
      // Bei Ctrl+S zeigen wir trotzdem eine Fehlermeldung an, auch wenn im silent mode
      showAlert(
        `⚠️ **Speichern fehlgeschlagen**\n\n${error.message || 'Unbekannter Fehler'}\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
        { title: "Fehler" }
      );
      return { success: false };
    }
  };

  // Plan in Datenbank speichern
  const handleDatabaseSaveSubmit = async (planNameValue, planDescriptionValue, selectedExistingPlan = null, silentMode = false, forceUpdate = false, updatePlanId = null, timeShiftData = null) => {
    if (!planNameValue.trim()) {
      if (!silentMode) {
        showAlert('Bitte geben Sie einen Plan-Namen ein.', { title: "Eingabe erforderlich" });
      }
      return false;
    }

    if (!silentMode) {
      showLoading('Speichere Plan in Datenbank...');
    }
    
    try {
      let panelGroupsData = [];
      
      if (panelGroupsContainerRef.current) {
        const containerData = panelGroupsContainerRef.current.getData();
        panelGroupsData = containerData.groups || [];
      }

      let saveData = {
        timestamp: new Date().toISOString(),
        startDate: startDate.toISOString(),
        version: "1.0",
        panelGroups: panelGroupsData
      };

      // Zeitliche Verschiebung anwenden (nur bei neuem Plan)
      if (timeShiftData && !selectedExistingPlan && !isUpdatingExistingPlan && !forceUpdate) {
        const shiftStart = new Date(timeShiftData.startDate);
        const shiftTarget = new Date(timeShiftData.targetDate);
        const daysDifference = Math.round((shiftTarget - shiftStart) / (1000 * 60 * 60 * 24));

        if (daysDifference !== 0) {
          saveData = shiftPlanDates(saveData, daysDifference);
        }
      }

      let result;
      
      if ((isUpdatingExistingPlan && existingPlanId) || (forceUpdate && updatePlanId)) {
        const planIdToUpdate = forceUpdate ? updatePlanId : existingPlanId;
        result = await updatePlan(planIdToUpdate, user.username, planNameValue.trim(), planDescriptionValue.trim(), saveData);
      } else {
        // Neuer Plan oder neue Version eines bestehenden Plans
        
        // Bei "Speichern als" (neuer Planname) soll sourcePlan mitgesendet werden
        let sourcePlan = null;
        if (!selectedExistingPlan && currentPlanName && currentPlanName !== 'unbenannt' && 
            currentPlanName !== planNameValue.trim() && currentLoadedPlanVersion) {
          sourcePlan = {
            planName: currentPlanName,
            planVersion: currentLoadedPlanVersion
          };
        }
        
        result = await savePlan(
          user.username, 
          planNameValue.trim(), 
          planDescriptionValue.trim(), 
          saveData, 
          selectedExistingPlan, // baseName für neue Version
          sourcePlan // sourcePlan für "Speichern als"
        );
      }
      
      // Prüfe ob result überhaupt eine gültige Antwort ist
      if (!result || typeof result !== 'object') {
        const errorMessage = 'Backend nicht erreichbar. Bitte prüfen Sie Ihre Verbindung.';
        if (!silentMode) {
          showAlert(
            `⚠️ **Speichern fehlgeschlagen**\n\n${errorMessage}\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
            { title: "Verbindungsfehler" }
          );
        } else {
          console.error('Stilles Speichern fehlgeschlagen: Backend nicht erreichbar');
        }
        return { success: false };
      }
      
      if (result.error) {
        const errorMessage = result.error === 'Fehler beim Speichern des Plans' 
          ? 'Backend nicht erreichbar. Bitte prüfen Sie Ihre Verbindung.' 
          : result.error;
        
        if (!silentMode) {
          showAlert(
            `⚠️ **Speichern fehlgeschlagen**\n\n${errorMessage}\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
            { title: "Fehler" }
          );
        } else {
          console.error('Stilles Speichern fehlgeschlagen:', errorMessage);
        }
        return { success: false };
      } else if (result.success) {
        if (!silentMode) {
          const message = selectedExistingPlan 
            ? `Neue Version von "${selectedExistingPlan}" erfolgreich gespeichert! (Version ${result.version})`
            : result.message || 'Plan erfolgreich gespeichert!';
          showAlert(message, { title: "Erfolg" });
          setShowDatabaseSaveDialog(false);
        }
        
        // Aktualisiere den Plannamen
        updatePlanName(planNameValue.trim());
        
        // ✅ Aktualisiere Plan-Metadaten mit neuer Version nach erfolgreichem Speichern
        updatePlanMetadata({
          planName: planNameValue.trim(),
          planVersion: result.version || 1,
          planId: result.planId,
          baseName: result.baseName || selectedExistingPlan || planNameValue.trim()
        });
        
        // Markiere als gespeichert
        markAsSaved();
        
        // Setze currentLoadedPlanId für nachfolgende Updates
        if (result.planId) {
          setCurrentLoadedPlanId(result.planId);
          // Speichere auch Plan-ID im localStorage für Session-Persistenz
          const planIdKey = `plan_id_${planNameValue.trim()}_${user.username}`;
          localStorage.setItem(planIdKey, String(result.planId)); // Als String speichern
        } else if (!currentLoadedPlanId && (isUpdatingExistingPlan && existingPlanId)) {
          // Falls keine planId zurückgegeben wird, aber wir ein Update machen
          setCurrentLoadedPlanId(existingPlanId);
        }
        
        // Callback aufrufen um Plan-Liste zu aktualisieren
        if (window.refreshPlans) {
          window.refreshPlans();
        }
        
        // Versionen für den betroffenen Plan neu laden, damit die neue Version sichtbar ist
        const baseNameToRefresh = result.baseName || selectedExistingPlan || planNameValue.trim();
        if (window.refreshPlanVersions && baseNameToRefresh) {
          window.refreshPlanVersions(baseNameToRefresh);
        }
        
        return { success: true, planId: result.planId };
      } else {
        const errorMessage = 'Unerwartete Antwort vom Server';
        if (!silentMode) {
          showAlert(
            `⚠️ **Speichern fehlgeschlagen**\n\n${errorMessage}\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
            { title: "Fehler" }
          );
        } else {
          console.error('Stilles Speichern: Unerwartete Antwort vom Server');
        }
        return { success: false };
      }
    } catch (error) {
      console.error('Fehler beim Datenbank-Speichern:', error);
      const errorMessage = error.message || 'Verbindungsfehler beim Speichern des Plans';
      if (!silentMode) {
        showAlert(
          `⚠️ **Speichern fehlgeschlagen**\n\n${errorMessage}\n\nWenn das Problem weiterhin besteht, erstellen Sie ein lokales Backup über "Backup lokal speichern".`,
          { title: "Verbindungsfehler" }
        );
      } else {
        console.error('Stilles Speichern fehlgeschlagen:', errorMessage);
      }
      return { success: false };
    } finally {
      if (!silentMode) {
        hideLoading();
      }
    }
  };

  // Plan aus Datenbank laden
  const handleLoadFromDatabase = async (planId, isPublished = false) => {
    if (!user?.username || !planId) {
      showAlert('Fehler: Benutzer oder Plan-ID fehlt', { title: "Fehler" });
      return;
    }

    showLoading('Lade Plan aus Datenbank...');
    
    try {
      // Verwende die richtige API je nach Plan-Typ
      const result = isPublished 
        ? await fetchPublishedPlan(planId, user.username)
        : await fetchPlan(planId, user.username);
      
      if (result.error) {
        showAlert('Fehler beim Laden: ' + result.error, { title: "Fehler" });
      } else if (result.success && result.plan) {
        const planData = result.plan.data;
        const planName = result.plan.name;
        const baseName = result.plan.base_name;
        const loadedPlanId = result.plan.id;
        const planVersion = result.plan.version;
        
        // ✅ Update Plan-Metadaten für Task-Zuordnungen
        updatePlanMetadata({
          planName: planName,
          planVersion: planVersion,
          planId: loadedPlanId,
          baseName: baseName
        });
        
        // Track loaded plan version for "Save As" functionality
        setCurrentLoadedPlanVersion(planVersion);
        
        // Verwende die tatsächlich geladene Version-ID (nicht automatisch die neueste)
        // Beim ersten Speichern wird dann eine neue Version erstellt
        let targetVersionId = loadedPlanId;
        
        // Behandle alle Pläne gleich - veröffentlichte werden zu privaten Plänen
        // Verwende einfach den originalen Plannamen ohne "(von ...)" Zusatz
        const displayName = planName;
        
        // Prüfe, ob bereits Daten vorhanden sind
        if (hasExistingData()) {
          setLoadMode('append');
          
          // Lade-Dialog mit Daten anzeigen
          setLoadJsonData(JSON.stringify(planData, null, 2));
          setShowLoadConfirmDialog(true);
          // Speichere den Plannamen und die Plan-ID für später
          window._pendingPlanName = displayName;
          window._pendingPlanId = isPublished ? null : targetVersionId; // Verwende die tatsächlich geladene Version
          window._isPublishedPlan = isPublished;
        } else {
          // Direktes Laden ohne Dialog
          try {
            setLoadMode('replace');
            window._pendingPlanName = displayName;
            
            // Behandlung je nach Plan-Typ
            if (isPublished) {
              // Veröffentlichte Pläne werden zu neuen privaten Plänen
              setCurrentLoadedPlanId(null);
              setCurrentLoadedPlanVersion(null);
              setHasCreatedVersionAfterLoad(false);
            } else {
              // Normale Pläne: Verwende die tatsächlich geladene Version-ID
              setCurrentLoadedPlanId(targetVersionId);
              setCurrentLoadedPlanVersion(planVersion);
              setHasCreatedVersionAfterLoad(false);
            }
            
            // Lösche localStorage-Einträge vom alten Plan beim Laden eines neuen Plans
            if (currentPlanName && user?.username) {
              const oldPlanSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
              const oldPlanIdKey = `plan_id_${currentPlanName}_${user.username}`;
              localStorage.removeItem(oldPlanSaveKey);
              localStorage.removeItem(oldPlanIdKey);
            }
            
            performLoad(planData, 'replace');
            markAsSaved();
            
            const successMessage = isPublished 
              ? `Veröffentlichter Plan "${displayName}" als privater Plan geladen!`
              : `Plan "${displayName}" erfolgreich geladen!`;
            showAlert(successMessage, { title: "Erfolg" });
          } catch (loadError) {
            showAlert('Fehler beim Laden des Plans: ' + loadError.message, { title: "Fehler" });
          }
        }
      } else {
        showAlert('Plan nicht gefunden oder keine Berechtigung', { title: "Fehler" });
      }
    } catch (error) {
      console.error('Fehler beim Laden aus Datenbank:', error);
      showAlert('Verbindungsfehler beim Laden des Plans', { title: "Verbindungsfehler" });
    } finally {
      hideLoading();
    }
  };

  // Plan für Update vorauswählen
  const handleUpdatePlan = async (planId, planName, planDescription) => {
    // Direkt neue Version speichern ohne Dialog zu zeigen
    const result = await handleDatabaseSaveSubmit(planName, planDescription, planName);
    return result && result.success;
  };

  // Direktes Datei-Laden (ohne Dialog)
  const handleDirectFileLoad = () => {
    // Erstelle ein verstecktes File Input Element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.syllabus,.json';
    input.style.display = 'none';
    
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.name.endsWith('.syllabus') && !file.name.endsWith('.json')) {
        showAlert('Bitte wählen Sie eine .syllabus oder .json Datei aus.', { title: "Ungültige Datei" });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const parsedData = JSON.parse(content);
          
          if (!parsedData.panelGroups || !Array.isArray(parsedData.panelGroups)) {
            throw new Error('Ungültige Datenstruktur: panelGroups fehlt oder ist kein Array');
          }

          // Extrahiere Dateiname ohne Erweiterung als Plannamen
          const fileName = file.name.replace(/\.(syllabus|json)$/, '');
          
          // Prüfe, ob bereits Daten vorhanden sind
          if (hasExistingData()) {
            // Zeige den Confirm-Dialog
            setLoadJsonData(content);
            setLoadMode('append'); // Standard-Modus
            window._pendingPlanName = fileName;
            setShowLoadConfirmDialog(true);
          } else {
            // Lade direkt ohne Abfrage
            setLoadMode('replace');
            setLoadJsonData(content);
            setPlanNameFromLoad(fileName);
            performLoad(parsedData);
          }
        } catch (error) {
          if (error instanceof SyntaxError) {
            showAlert('Ungültiger JSON-Code. Bitte überprüfen Sie die Syntax (Kommas, Anführungszeichen, Klammern).', { title: "Ungültiges JSON" });
          } else {
            showAlert('Fehler beim Lesen der Datei: ' + error.message, { title: "Fehler" });
          }
        }
      };
      reader.readAsText(file);
    };

    // Klick simulieren
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleDownloadFile = (fileName) => {
    if (!fileName || !fileName.trim()) {
      fileName = currentPlanName || 'syllabus_export';
    }

    try {
      const fullFileName = fileName.trim() + '.syllabus';
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fullFileName;
      link.style.display = 'none'; // Verstecken des Links
      
      document.body.appendChild(link);
      link.click();
      
      // Kurz warten, bevor der Link entfernt wird
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setShowSaveDialog(false); // Dialog nach Download schließen
        
        // Markiere als gespeichert
        markAsSaved();
      }, 100);
    } catch (error) {
      showAlert('Fehler beim Download: ' + error.message, { title: "Fehler" });
    }
  };

  return {
    showSaveDialog,
    setShowSaveDialog,
    showLoadDialog,
    setShowLoadDialog,
    showLoadConfirmDialog,
    setShowLoadConfirmDialog,
    loadMode,
    setLoadMode,
    jsonData,
    loadJsonData,
    setLoadJsonData,
    saveFileName,
    setSaveFileName,
    handleSave,
    handleLoad,
    handleFileUpload,
    handleCancelLoad,
    handleLoadSubmit,
    handleLoadConfirm,
    handleLoadConfirmCancel,
    handleDownloadFile,
    
    // New database save/load
    showDatabaseSaveDialog,
    setShowDatabaseSaveDialog,
    planName,
    setPlanName,
    planDescription,
    setPlanDescription,
    isUpdatingExistingPlan,
    handleDatabaseSave,
    handleDatabaseSaveSubmit,
    handleCtrlSave,
    handleSilentSave,
    handleDirectFileLoad,
    handleLoadFromDatabase,
    handleUpdatePlan,
    currentPlanName, // Aktueller Plan-Name für das SaveDialog
    currentLoadedPlanId, // ID des aktuell geladenen Plans für Highlight
    setCurrentLoadedPlanId, // Funktion zum Setzen der aktuell geladenen Plan-ID
    
    // Neue Plan-Funktionen
    createNewPlan: () => {
      // Erstellt einen neuen Plan und setzt entsprechenden Namen
      const newName = createNewPlan();
      
      // Lösche localStorage-Einträge für Session-Persistenz vom alten Plan
      if (currentPlanName && user?.username) {
        const oldPlanSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
        const oldPlanIdKey = `plan_id_${currentPlanName}_${user.username}`;
        localStorage.removeItem(oldPlanSaveKey);
        localStorage.removeItem(oldPlanIdKey);
      }
      
      // Reset Zustand für neuen Plan
      setCurrentLoadedPlanId(null);
      setCurrentLoadedPlanVersion(null);
      setHasCreatedVersionAfterLoad(false);
      
      return newName;
    },
    
    // Funktion zum Löschen der localStorage-Einträge beim Plan-Namen-Wechsel
    clearPlanSessionData: (oldPlanName) => {
      if (oldPlanName && user?.username) {
        const planSaveKey = `plan_saved_${oldPlanName}_${user.username}`;
        const planIdKey = `plan_id_${oldPlanName}_${user.username}`;
        localStorage.removeItem(planSaveKey);
        localStorage.removeItem(planIdKey);
      }
    }
  };
};
